import { Injectable } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ActivatedRouterStateSnapshot,
  CurrencyService,
  LanguageService,
  ProductSearchService,
  RouterState,
  RoutingService,
} from '@spartacus/core'
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  pluck,
  shareReplay,
  tap,
} from 'rxjs/operators'
import { ProductSearchPage } from '../../../models/product-search.model'
import {
  ProductListRouteParams,
  SearchCriteria,
} from '../../../models/product-list.model'

/**
 * The `ProductListComponentService` is used to search products. The service is used
 * on the Product Listing Page, for listing products and the facet navigation.
 *
 * The service exposes the product search results based on the category and search
 * route parameters. The route parameters are used to query products by the help of
 * the `ProductSearchService`.
 */
@Injectable({ providedIn: 'root' })
export class ProductListComponentService {
  /**
   * @deprecated will be removed in version 3.0 as this is the
   *   subscription is longer used
   */
  protected sub: Subscription

  // TODO: make it configurable
  public defaultPageSize = 50

  //protected readonly RELEVANCE_ALLCATEGORIES = ':relevance:allCategories:';
  public sortlisted: string = 'relevance'

  public pageDefaultSource = new BehaviorSubject(50)
  getDefaultPage = this.pageDefaultSource.asObservable()

  public sortCodeDefaultSource = new BehaviorSubject('')
  getSortCode = this.sortCodeDefaultSource.asObservable()

  constructor(
    protected productSearchService: ProductSearchService,
    protected routing: RoutingService,
    protected activatedRoute: ActivatedRoute,
    protected currencyService: CurrencyService,
    protected languageService: LanguageService,
    protected router: Router
  ) {}

  /**
   * Emits the search results for the current search query.
   *
   * The `searchResults$` is _not_ concerned with querying, it only observes the
   * `productSearchService.getResults()`
   */
  protected searchResults$: Observable<ProductSearchPage | any> =
    this.productSearchService
      .getResults()
      .pipe(filter((searchResult) => Object.keys(searchResult).length > 0))

  /**
   * Observes the route and performs a search on each route change.
   *
   * Context changes, such as language and currencies are also taken
   * into account, so that the search is performed again.
   */
  protected searchByRouting$: Observable<ActivatedRouterStateSnapshot> =
    combineLatest([
      this.routing.getRouterState().pipe(
        distinctUntilChanged((x, y) => {
          // router emits new value also when the anticipated `nextState` changes
          // but we want to perform search only when current url changes
          return x.state.url === y.state.url
        })
      ),
      ...this.siteContext,
    ]).pipe(
      map(([routerState, ..._context]) => (routerState as RouterState).state),
      tap((state: ActivatedRouterStateSnapshot) => {
        const criteria = this.getCriteriaFromRoute(
          state.params,
          state.queryParams
        )
        this.search(criteria)
      })
    )

  /**
   * This stream is used for the Product Listing and Product Facets.
   *
   * It not only emits search results, but also performs a search on every change
   * of the route (i.e. route params or query params).
   *
   * When a user leaves the PLP route, the PLP component unsubscribes from this stream
   * so no longer the search is performed on route change.
   */
  readonly model$: Observable<ProductSearchPage> = combineLatest([
    this.searchResults$,
    this.searchByRouting$,
  ]).pipe(pluck(0), shareReplay({ bufferSize: 1, refCount: true }))

  /**
   * Expose the `SearchCriteria`. The search criteria are driven by the route parameters.
   *
   * This search route configuration is not yet configurable
   * (see https://github.com/SAP/spartacus/issues/7191).
   */
  protected getCriteriaFromRoute(
    routeParams: ProductListRouteParams,
    queryParams: SearchCriteria
  ): SearchCriteria {
    return {
      query: queryParams.query || this.getQueryFromRouteParams(routeParams),
      pageSize: queryParams.pageSize,
      currentPage: queryParams.currentPage,
      sortCode: queryParams.sortCode,
    }
  }

  /**
   * Resolves the search query from the given `ProductListRouteParams`.
   */
  protected getQueryFromRouteParams({
    query,
    categoryCode,
    brandCode,
  }: ProductListRouteParams) {
    if (query) {
      return query
    }
    if (categoryCode) {
      return ':' + this.sortlisted + ':allCategories:' + categoryCode
    }

    // TODO: drop support for brands as they should be treated
    // similarly as any category.
    if (brandCode) {
      return ':' + this.sortlisted + ':allCategories:' + brandCode
    }
  }

  changeDefaultPageSize(pageSize: number) {
    this.defaultPageSize = pageSize
    // this.pageChange(this.defaultPageSize)
  }
  /**
   * Performs a search based on the given search criteria.
   *
   * The search is delegated to the `ProductSearchService`.
   */
  protected search(criteria: SearchCriteria): void {
    const currentPage = criteria.currentPage
    const pageSize = criteria.pageSize
    const sortCode = criteria.sortCode

    this.productSearchService.search(
      criteria.query,
      // TODO: consider dropping this complex passing of cleaned object
      Object.assign(
        {},
        currentPage && { currentPage },
        pageSize && { pageSize },
        sortCode && { sortCode }
      )
    )
  }

  /**
   * Get items from a given page without using navigation
   */
  getPageItems(pageNumber: number): void {
    this.routing
      .getRouterState()
      .subscribe((route) => {
        const routeCriteria = this.getCriteriaFromRoute(
          route.state.params,
          route.state.queryParams
        )
        const criteria = {
          ...routeCriteria,
          currentPage: pageNumber,
        }
        this.search(criteria)
      })
      .unsubscribe()
  }

  /**
   * Sort the search results by the given sort code.
   */
  sort(sortCode: string, routes?: ActivatedRoute): void {
    this.route({ sortCode }, routes || this.activatedRoute)
    this.sortlisted = sortCode
  }
  pageChange(pageSize, routes?: ActivatedRoute) {
    this.route({ pageSize: pageSize }, routes || this.activatedRoute)
  }
  /**
   * Routes to the next product listing page, using the given `queryParams`. The
   * `queryParams` support sorting, pagination and querying.
   *
   * The `queryParams` are delegated to the Angular router `NavigationExtras`.
   */
  route(queryParams: SearchCriteria, routes?: ActivatedRoute): void {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      relativeTo: routes || this.activatedRoute,
    })
  }

  /**
   * The site context is used to update the search query in case of a
   * changing context. The context will typically influence the search data.
   *
   * We keep this private for now, as we're likely refactoring this in the next
   * major version.
   */
  private get siteContext(): Observable<string>[] {
    // TODO: we should refactor this so that custom context will be taken
    // into account automatically. Ideally, we drop the specific context
    // from the constructor, and query a ContextService for all contexts.

    return [this.languageService.getActive(), this.currencyService.getActive()]
  }

  /**
   * @deprecated will be dropped in version 3.0 as it's no longer in use
   */
  setQuery(query: string): void {
    this.route({
      query,
      currentPage: undefined,
      pageSize: this.defaultPageSize,
    }, this.activatedRoute)
  }

  /**
   * @deprecated will be dropped in version 3.0 as it's no longer in use
   */
  viewPage(pageNumber: number): void {
    this.route({ currentPage: pageNumber }, this.activatedRoute)
  }

  setPagetoDefault(pageSize) {
    this.pageDefaultSource.next(pageSize)
  }

  setSortCode(sortCode) {
    this.sortlisted = sortCode
    this.sortCodeDefaultSource.next(sortCode)
  }

  retainSortCode() {
    return this.sortlisted
  }
}
